import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Wallet,
  TrendingUp,
  TrendingDown,
  Download,
  Calendar,
  Search,
  Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { formatRupiah, formatRupiahShort, formatPercent, formatChange } from '../utils/format';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/analytics/financial`),
        axios.get(`${API}/transactions`)
      ]);
      
      setAnalytics(analyticsRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const EXPENSE_COLORS = ['#1A56DB', '#EF4444', '#10B981', '#94A3B8'];

  const expenseData = analytics?.expense_breakdown ? 
    Object.entries(analytics.expense_breakdown).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1).replace('_', ' '),
      value: typeof value === 'number' && value <= 100 ? value : 25
    })) : 
    [
      { name: 'Maintenance', value: 45 },
      { name: 'Taxes & Insurance', value: 25 },
      { name: 'Staff & Ops', value: 15 },
      { name: 'Marketing', value: 15 }
    ];

  const totalExpense = expenseData.reduce((sum, item) => sum + item.value, 0);

  const getCategoryBadge = (category) => {
    switch (category) {
      case 'rent_payment':
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Rent Payment</Badge>;
      case 'maintenance':
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Plumbing Repair</Badge>;
      case 'taxes':
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Taxes</Badge>;
      case 'utilities':
        return <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">Utility Fees</Badge>;
      default:
        return <Badge variant="secondary">{category}</Badge>;
    }
  };

  const getStatusIndicator = (status) => {
    switch (status) {
      case 'completed':
        return <span className="flex items-center gap-1.5 text-green-600"><span className="w-1.5 h-1.5 bg-green-500 rounded-full" /> Completed</span>;
      case 'processing':
        return <span className="flex items-center gap-1.5 text-amber-600"><span className="w-1.5 h-1.5 bg-amber-500 rounded-full" /> Processing</span>;
      case 'failed':
        return <span className="flex items-center gap-1.5 text-red-600"><span className="w-1.5 h-1.5 bg-red-500 rounded-full" /> Failed</span>;
      default:
        return <span className="text-slate-500">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1A56DB]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Financial Analytics
          </h1>
          <p className="text-slate-500 mt-1">Fiscal Year 2024 Performance Overview</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="gap-2" data-testid="date-range-btn">
            <Calendar className="w-4 h-4" />
            Last 12 Months
          </Button>
          <Button className="gap-2 bg-[#1A56DB] hover:bg-[#1E40AF]" data-testid="export-report-btn">
            <Download className="w-4 h-4" />
            Export Report
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Revenue */}
        <div className="metric-card" data-testid="metric-revenue">
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 bg-[#EFF6FF] rounded-lg flex items-center justify-center">
              <Wallet className="w-5 h-5 text-[#1A56DB]" />
            </div>
            <Badge variant="outline" className="text-green-600 bg-green-50 border-green-200 gap-1">
              <TrendingUp className="w-3 h-3" />
              {formatChange(analytics?.revenue_change)}
            </Badge>
          </div>
          <p className="text-sm text-slate-500 mb-1">Total Revenue</p>
          <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {formatRupiah(analytics?.total_revenue)}
          </p>
        </div>

        {/* Total Expenses */}
        <div className="metric-card" data-testid="metric-expenses">
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-red-500" />
            </div>
            <Badge variant="outline" className="text-red-600 bg-red-50 border-red-200 gap-1">
              <TrendingDown className="w-3 h-3" />
              {formatChange(analytics?.expense_change)}
            </Badge>
          </div>
          <p className="text-sm text-slate-500 mb-1">Total Expenses</p>
          <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {formatRupiah(analytics?.total_expenses)}
          </p>
        </div>

        {/* Net Profit */}
        <div className="metric-card" data-testid="metric-profit">
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <Badge variant="outline" className="text-green-600 bg-green-50 border-green-200 gap-1">
              <TrendingUp className="w-3 h-3" />
              {formatChange(analytics?.profit_change)}
            </Badge>
          </div>
          <p className="text-sm text-slate-500 mb-1">Net Profit</p>
          <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {formatRupiah(analytics?.net_profit)}
          </p>
        </div>

        {/* Profit Margin */}
        <div className="metric-card" data-testid="metric-margin">
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-slate-600" />
            </div>
            <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded">Optimal</span>
          </div>
          <p className="text-sm text-slate-500 mb-1">Profit Margin</p>
          <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {formatPercent(analytics?.profit_margin)}
          </p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue vs Expenses Chart */}
        <div className="lg:col-span-2 chart-container" data-testid="revenue-expenses-chart">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Revenue vs Expenses
              </h3>
              <p className="text-sm text-slate-500">Monthly breakdown for current fiscal year</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#1A56DB] rounded-full" />
                <span className="text-sm text-slate-600">Revenue</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded-full" />
                <span className="text-sm text-slate-600">Expense</span>
              </div>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics?.monthly_data || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: '#64748B', fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis 
                  tick={{ fill: '#64748B', fontSize: 12 }} 
                  axisLine={false} 
                  tickLine={false}
                  tickFormatter={(value) => `${value / 1000000}M`}
                />
                <Tooltip 
                  formatter={(value, name) => [formatRupiahShort(value), name === 'revenue' ? 'Revenue' : 'Expense']}
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                  }}
                />
                <Bar dataKey="revenue" fill="#1A56DB" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expense" fill="#EF4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Expense Breakdown */}
        <div className="chart-container" data-testid="expense-breakdown">
          <div className="mb-6">
            <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Expense Breakdown
            </h3>
            <p className="text-sm text-slate-500">Distribution of costs by category</p>
          </div>
          <div className="h-48 relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={expenseData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {expenseData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={EXPENSE_COLORS[index % EXPENSE_COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <p className="text-xs text-slate-500">TOTAL</p>
                <p className="font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {formatRupiahShort(analytics?.total_expenses || 485000000)}
                </p>
              </div>
            </div>
          </div>
          <div className="space-y-2 mt-4">
            {expenseData.map((item, index) => (
              <div key={item.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-2.5 h-2.5 rounded-full" 
                    style={{ backgroundColor: EXPENSE_COLORS[index % EXPENSE_COLORS.length] }}
                  />
                  <span className="text-slate-600">{item.name}</span>
                </div>
                <span className="font-medium text-slate-900">{item.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Transaction Log */}
      <div className="chart-container" data-testid="transaction-log">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Transaction Log
          </h3>
          <Button variant="link" className="text-[#1A56DB] p-0" data-testid="view-all-transactions">
            View All
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Transaction ID</th>
                <th>Property Entity</th>
                <th>Date</th>
                <th>Category</th>
                <th>Status</th>
                <th>Amount (Rp)</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => (
                <tr key={tx.id} data-testid={`transaction-row-${tx.id}`}>
                  <td className="font-medium text-slate-900">{tx.transaction_id}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-slate-100 rounded flex items-center justify-center">
                        <span className="text-xs text-slate-500">●</span>
                      </div>
                      <span>{tx.property_name}</span>
                    </div>
                  </td>
                  <td className="text-slate-600">{tx.date}</td>
                  <td>{getCategoryBadge(tx.category)}</td>
                  <td>{getStatusIndicator(tx.status)}</td>
                  <td className={`font-medium ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {tx.amount >= 0 ? '+' : ''}{formatRupiah(tx.amount).replace('Rp ', '')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
